import api from "@shared/api/client";
import type { AddressProviderUpdateData } from "@shared/types/api";
import type { AddressProvider } from "@shared/types/models";
import { extractErrorMessage, toOptionalNumber, toOptionalString } from "@shared/utils";
import type { TFunction } from "i18next";
import { useCallback } from "react";
import type { AddressesState, SetAddressesState } from "./types";

const useProviderAssignment = (
  assignState: AddressesState["assignState"],
  editProviderState: AddressesState["editProviderState"],
  setState: SetAddressesState,
  fetchData: () => Promise<void>,
  t: TFunction,
) => {
  const openAssign = useCallback(
    (addressId: number) => {
      setState({
        assignState: {
          open: true,
          addressId,
          providerId: "",
          username: "",
          password: "",
          accountKeyword: "",
          skipPayment: false,
          taxDeduction: false,
          taxDeductionAmount: "",
          meterReadingEnabled: false,
          invoiceEmailTo: "",
        },
      });
    },
    [setState],
  );

  const handleAssign = useCallback(async () => {
    if (!assignState.addressId || !assignState.providerId) return;
    try {
      await api.addresses.assignProvider(assignState.addressId, {
        providerId: parseInt(assignState.providerId, 10),
        username: toOptionalString(assignState.username),
        password: toOptionalString(assignState.password),
        accountKeyword: toOptionalString(assignState.accountKeyword),
        skipPayment: assignState.skipPayment,
        taxDeduction: assignState.taxDeduction,
        taxDeductionAmount: assignState.taxDeduction ? toOptionalNumber(assignState.taxDeductionAmount) : undefined,
        meterReadingEnabled: assignState.meterReadingEnabled,
        invoiceEmailTo: toOptionalString(assignState.invoiceEmailTo),
      });
      setState({ assignState: { ...assignState, open: false } });
      await fetchData();
    } catch (err: unknown) {
      alert(extractErrorMessage(err, t("addresses.assignError")));
    }
  }, [assignState, setState, fetchData, t]);

  const handleRemoveProvider = useCallback(
    async (addressId: number, providerId: number) => {
      try {
        await api.addresses.removeProvider(addressId, providerId);
        await fetchData();
      } catch (err: unknown) {
        alert(extractErrorMessage(err, t("addresses.removeError")));
      }
    },
    [fetchData, t],
  );

  const openEditProvider = useCallback(
    (ap: AddressProvider) => {
      setState({
        editProviderState: {
          open: true,
          addressId: ap.addressId,
          providerId: ap.providerId,
          username: ap.username || "",
          password: ap.password || "",
          accountKeyword: ap.accountKeyword || "",
          skipPayment: ap.skipPayment,
          taxDeduction: ap.taxDeduction,
          taxDeductionAmount: String(ap.taxDeductionAmount ?? ""),
          hasPassword: ap.password !== null,
          meterReadingEnabled: ap.meterReadingEnabled,
          invoiceEmailTo: ap.invoiceEmailTo || "",
        },
      });
    },
    [setState],
  );

  const handleEditSave = useCallback(async () => {
    if (editProviderState.addressId === null || editProviderState.providerId === null) return;
    try {
      const data: AddressProviderUpdateData = {
        username: toOptionalString(editProviderState.username),
        accountKeyword: toOptionalString(editProviderState.accountKeyword),
        skipPayment: editProviderState.skipPayment,
        taxDeduction: editProviderState.taxDeduction,
        taxDeductionAmount: editProviderState.taxDeduction ? toOptionalNumber(editProviderState.taxDeductionAmount) : undefined,
        meterReadingEnabled: editProviderState.meterReadingEnabled,
        invoiceEmailTo: toOptionalString(editProviderState.invoiceEmailTo),
      };
      if (editProviderState.password) data.password = editProviderState.password;
      await api.addresses.updateProvider(editProviderState.addressId, editProviderState.providerId, data);
      setState({ editProviderState: { ...editProviderState, open: false } });
      await fetchData();
    } catch (err: unknown) {
      alert(extractErrorMessage(err, t("addresses.updateError")));
    }
  }, [editProviderState, setState, fetchData, t]);

  return { openAssign, handleAssign, handleRemoveProvider, openEditProvider, handleEditSave };
};

export { useProviderAssignment };
